import HelpIcon from '@mui/icons-material/Help';
import {
	Box,
	Button,
	Card,
	CardContent,
	IconButton,
	Breadcrumbs as MUIBreadcrumbs,
	Link as MuiLink,
	Stack,
	Typography,
	Grid,
	useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUniqueEffect } from '../../hooks';
import util from '../../util/index';
import HelpForm from '../../views/forms/HelpForm';
import consts from '../../consts';
import { usePrerequisites } from '../../contexts/Prerequisites';
import DocumentTitle from './DocumentTitle';

const BreadcrumbsComponent = ({
	setContextValues,
	breadcrumbs = [],
	docTitle,
	actions = [],
	subbreadcrumbs = [],
	hide,
	title: customTitle,
	infoMessage = { isvisible: false, value: '' },
	...rest
}) => {
	const location = useLocation();
	const navigate = useNavigate();
	const theme = useTheme();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [hideHelp, setHideHelp] = useState(false)
	const pathname = location.pathname;
	const noHelpPaths = ['/videolist', '/setup'];
	const noBreadcrumbPaths = ['/videolist'];
	const { menu, tags = {} } = { ...util.localData?.loginData };
	const isAdminOrViewer = consts.regex.AdminOrViewer.test(tags.RoleName);
	const isSmallScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));
	const actionsPerRow = 3;
	const [{ strings }] = usePrerequisites();
	const flattenItems = (items) => {
		let flatItems = [];
		if (Array.isArray(items)) {
			for (const item of items) {
				if (Array.isArray(item?.children)) {
					flatItems = flatItems.concat(flattenItems(item.children));
				} else {
					flatItems.push(item);
				}
			}
		} else {
			flatItems.push(items);
		}
		return flatItems;
	};
	const chunkedActions = useMemo(() => {
		const chunks = [];
		for (let i = 0; i < actions.length; i += actionsPerRow) {
			const chunk = actions.slice(i, i + actionsPerRow);
			const isViewChunk = chunk[0]?.title === "Process Monitor";
			const isPrint = chunk[0]?.title === "Print";
			if (isViewChunk) {
				chunks.push(
					chunk.map((item, index) => ({
						...item,
						style: {
							marginLeft: index <= 4 ? "8px" : 0,
						},
					}))
				);
			} else if (isPrint) {
				chunks.push(
					chunk.map((item, index) => ({
						...item,
						style: {
							marginLeft: index <= 3 ? 0 : 0,
						},
					}))
				);
			} else {
				chunks.push(
					chunk.map((item, index) => ({
						...item,
						style: {
							marginLeft: index <= 3 ? "5px" : "0",
						},
					}))
				)
			}
		}
		return chunks;
	}, [actions, actionsPerRow]);
	const flatMenuItems = useMemo(() => flattenItems(menu?.children), [menu?.children]);
	const locationItem = useMemo(
		() => flatMenuItems.filter((item) => `/${item?.url?.toLowerCase()}` === location?.pathname?.toLowerCase())[0],
		[location?.pathname]
	);
	const spacing = theme.spacing();


	useUniqueEffect(() => {
		setIsModalOpen(false);
		setHideHelp(noHelpPaths.includes(pathname.toLowerCase()))
		if (typeof setContextValues === 'function') {
			return () => {
				setContextValues({
					hide: false,
					docTitle: null,
					breadcrumbs: [],
					actions: [],
					subbreadcrumbs: [],
					infoMessage: { isvisible: false, value: '' },
				});
			}
		}
	}, [pathname]);

	let label = strings[locationItem?.text] || locationItem?.text;

	label = label || customTitle;

	const title = strings[docTitle] || docTitle || strings[label] || label || (breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1].text : null);
	const helpFormTitle = useMemo(() => {
		let str = label
		if (breadcrumbs?.length > 0) {
			str = breadcrumbs[breadcrumbs.length - 1].text;
		}
		str = strings[str] || str
		return str;
	}, [breadcrumbs, label])

	const openHelp = () => { setIsModalOpen(true); }
	const lastBreadCrumbIndex = (breadcrumbs?.length || 0) - 1;
	const showBreadcrumb = !hide && !noBreadcrumbPaths.includes(location.pathname.toLowerCase());

	return (
		showBreadcrumb && (
			<div>
				<Card
					sx={{
						marginBottom: theme.spacing(),
						'& .MuiCardContent-root:last-child': {
							paddingBottom: theme.spacing()
						}
					}}
				>
					<CardContent sx={{ padding: theme.spacing(), paddingBottom: `${theme.spacing()}px !important` }}>
						{!!title && (
							<Grid container>
								<Grid size={12}>
									<DocumentTitle
										title={`${strings.PageHeader || ''} ${strings.PageHeader && title ? '|' : ''} ${title}`}
									/>
								</Grid>
							</Grid>
						)}
						<Grid container>
							<Grid size={'auto'} sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
								<Grid container>
									<Grid size={12}>
										<MUIBreadcrumbs
											aria-label="breadcrumb"
											sx={{ fontSize: (theme) => theme.spacing(3.5) }}
										>
											{Array.isArray(breadcrumbs) &&
												breadcrumbs?.length > 0 &&
												breadcrumbs.map(({ text, ...params }, i) =>
													i < lastBreadCrumbIndex ? (
														<MuiLink
															color="primary"
															fontSize="inherit"
															underline="none"
															component={Link}
															{...params}
															key={`breadcrumb-${i}`}
														>
															{strings[text?.replace(/\s|-|!|—|[/]|[\\]/gi, '')] || text}
														</MuiLink>
													) : (
														<Typography
															color="primary"
															fontSize="inherit"
															component="span"
															sx={{ fontWeight: 400 }}
															{...params}
															key={`breadcrumb-text${i}`}
														>
															{strings[text?.replace(/\s|-|!|—|[/]|[\\]/gi, '')] || text}
														</Typography>
													)
												)}
											{!breadcrumbs?.length > 0 && label && (
												<Typography
													color="primary"
													fontSize="inherit"
													component="span"
													sx={{ fontWeight: 400 }}
												>
													{label}
												</Typography>
											)}
										</MUIBreadcrumbs>
									</Grid>
									{subbreadcrumbs?.length > 0 && (
										<Grid
											size={12}
											sx={{
												'& .subbreadcrumb': {
													textDecoration: 'none'
												},
												width: 'max-content'
											}}

										>
											<MUIBreadcrumbs>
												{subbreadcrumbs.map(({ className, text, ...params }, i) => (
													<Typography
														color={params?.to ? 'primary' : 'textSecondary'}
														fontSize="inherit"
														className={`subbreadcrumb ${className || ''}`}
														component={params?.to ? Link : 'span'}
														{...params}
														key={`subbreadcrumb-${i}`}
													>
														{text}
													</Typography>
												))}
											</MUIBreadcrumbs>
										</Grid>
									)}
								</Grid>
							</Grid>
							{isSmallScreen ? (
								chunkedActions.map((chunk, chunkIndex) => (
									<Stack key={`chunk-${chunkIndex}`} spacing={1} direction="row" alignItems="center">
										{chunk.map(({ text, children, component: Component = Button, spacer, ...actionProps }, index) => (
											<Box
												key={`action-${chunkIndex}-${index}`}
												sx={{
													marginLeft: index > 0 && index % 4 === 0 ? '2px' : 0,
												}}
											>
												<Component {...actionProps}>
													{text || children}
												</Component>
											</Box>
										))}
									</Stack>
								))
							) : (
								Array.isArray(actions) && (
									<Stack spacing={1} direction="row" alignItems="center">
										{actions.map(({ text, children, component: Component = Button, ...actionProps }, index) => (
											<Component {...actionProps} key={`breadcrumbs-action-${index}`}>
												{text || children}
											</Component>
										))}
									</Stack>
								)
							)}

							{!hideHelp && (
								<Grid
									size={'auto'}
									sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}
									className="no-print"
									data-html2canvas-ignore="true"
								>
									{!isAdminOrViewer && (
										<IconButton onClick={openHelp} color="primary" title={strings.Help} size="large">
											<HelpIcon fontSize="inherit" />
										</IconButton>
									)}
									<HelpForm
										navigate={navigate}
										locationLabel={helpFormTitle}
										filename={locationItem?.url}
										isOpen={isModalOpen} // Pass the state to control modal visibility
										onClose={() => setIsModalOpen(false)} // Provide a method to close the modal
									/>
								</Grid>
							)}
						</Grid>
					</CardContent>
				</Card>
				{infoMessage.isvisible && (  // Check if infoMessage.isvisible is true
					<Card
						sx={{
							marginBottom: spacing,
							'& .MuiCardContent-root:last-child': {
								paddingBottom: spacing
							},
						}}
					>
						<CardContent sx={{ padding: spacing, paddingBottom: `${spacing}px !important` }}>
							<Typography variant="h5" sx={{ color: 'red', fontWeight: 400 }}>
								{infoMessage.value}
							</Typography>
						</CardContent>
					</Card>
				)}
			</div >
		)
	);
};


export default BreadcrumbsComponent;
